/* eslint-disable import/no-extraneous-dependencies */
import { mapGetters, mapMutations } from 'vuex';
import { VUEX_GENERAL, VUEX_CHANGE_COVER } from '@/constants/VuexConstants';
import { DOBErrorMessage, ProductType, RelationshipValue, WhoIsEligibleString, StateCodes } from '@general/js/enums';
import { calculateAge, DHLError, getCurrentDate, isPastDate, reverseFormatFormDate } from '@general/js/utils';
import { TransferToFundReason } from '@general/js/constants';
import { CoverCompositionCRMCodeMapping, OperationType, ProductExcessType } from '@/enums';
import { convertCodeToValue, isEmptyString, getPartnerDetailsMP, getSelectOptionsLeft } from '@/utils';
import Services from '@/services/MyCover/ChangeCover';

const ABD_ELIGIBILITY = 'isEligibleForAbd';

export default {
  data() {
    return {
      apiStatus: {
        duplicatePersonCheck: {
          loading: false,
          fail: false,
        },
      },
      transferToFundValues: TransferToFundReason,
      isOvercovered: false,
      stateOptions: StateCodes,
      excessOptions: [
        { code: 1, value: '0' },
        { code: 2, value: '250' },
        { code: 3, value: '500' },
        { code: 4, value: '700' },
        { code: 5, value: '750' },
        { code: 6, value: '200' },
        { code: 7, value: '300' },
        { code: 8, value: '400' },
        { code: 9, value: '800' },
      ],
      productExcessTypeOptions: [
        { code: 527340000, vale: 'None' },
        { code: 527340001, value: ProductExcessType.POLICY_EXCESS },
        { code: 527340002, value: ProductExcessType.MEMBER_EXCESS },
      ],
      CHILDREN_NOT_SUPPORTED: `<p>Based on the current product you are on, you are unable to add a child to this policy.</p>`,
      ENFORCE_CHILDREN_ERROR_MESSAGE: `<p>You are unable to proceed with removing this child on your current policy without replacing them.</p>`,
      ENFORCE_PARTNER_ERROR_MESSAGE: `<p>You are unable to proceed with removing this partner on your current policy without replacing them.</p>`,
      PARTNER_NOT_SUPPORTED: `<p>Based on the current product you are on, you are unable to add a partner to this policy.</p>`,
      DUPLICATE_PERSON_ERROR_MESSAGE: `<p>There has been an error with adding this person to this policy, a Defence Health team member will contact you shortly to assist you.</p>`,
      OVER_COVERAGE_ERROR_MESSAGE: `<p>This member already holds similar cover and cannot be added to this policy.</p><p>Please contact the member to discuss their options:</p><ul><li>Terminating their existing cover and joining this membership</li><li>Remain on existing membership</li></ul>`,
      COMPOSITION_NOT_SUPPORTED: `<p>Based on your current product you are unable to make this change to this policy.</p>`,
      PARTNER_REBATE_HOLDER_ERROR_MESSAGE: `<p>You can’t remove the partner on this policy as they currently hold the rebate on this policy. Please contact Defence Health to either switch the rebate applicant, withdraw the rebate or terminate the partner on the policy.</p>`,
    };
  },

  computed: {
    ...mapGetters({
      personCode: VUEX_GENERAL.GETTERS.getCurrentPersonCode,
      memberNumber: VUEX_GENERAL.GETTERS.getCurrentPolicyNumber,
      members: VUEX_GENERAL.GETTERS.getMembers,
      currentMembership: VUEX_GENERAL.GETTERS.getCurrentMembership,
      membershipInfo: VUEX_GENERAL.GETTERS.getMembershipInfo,
    }),
    ...mapGetters('AddRemovePerson', {
      partnerData: VUEX_CHANGE_COVER.GETTERS.getPartnerData,
      childrenData: VUEX_CHANGE_COVER.GETTERS.getChildrenData,
      allMembersData: VUEX_CHANGE_COVER.GETTERS.getAllMembers,
      currentComposition: VUEX_CHANGE_COVER.GETTERS.getCurrentComposition,
      membershipPolicies: VUEX_CHANGE_COVER.GETTERS.getMembershipPolicies,
      membershipDetails: VUEX_CHANGE_COVER.GETTERS.getMembershipDetails,
      currentEligibility: VUEX_CHANGE_COVER.GETTERS.getCurrentEligibility,
      eligibilityDetails: VUEX_CHANGE_COVER.GETTERS.getEligibilityDetails,
      referenceData: VUEX_CHANGE_COVER.GETTERS.getReferenceData,
      submitPolicyInProcess: VUEX_CHANGE_COVER.GETTERS.getSubmitPolicyInProcess,
    }),

    // this property returns an array of all supported compositions for the current products
    compositionsAllowedForCurrentProducts() {
      const supportedCompositions = [];

      // if the current cover is Extras Only, then all compositions are allowed since Extras only doesn't have any composition/excess restrictions
      if (this.coverExtrasOnly) {
        return [
          ...CoverCompositionCRMCodeMapping.map((item) => {
            return item.value;
          }),
        ];
      }

      if (this.membershipPolicies) {
        this.membershipPolicies.forEach((product) => {
          if (product.productExcess && product.productExcess.length > 0) {
            product.productExcess.forEach((excess) => {
              excess.composition.split(',').forEach((code) => {
                supportedCompositions.push(
                  CoverCompositionCRMCodeMapping.find((value) => {
                    return value.code === code;
                  }).value ?? null,
                );
              });
            });
          }
        });
      }

      return [...new Set(supportedCompositions)];
    },

    oldCompositionHasPartner() {
      if (this.allMembersData) {
        const partner = getPartnerDetailsMP(this.allMembersData, true);

        if (partner) {
          return true;
        }
      }

      return false;
    },

    isPolicyHolderEligible() {
      return this.eligibilityDetails?.whoIsEligible.toLowerCase() === WhoIsEligibleString.SELF;
    },

    eligibleForABD() {
      const products = this.membershipPolicies?.map((obj) => {
        return Boolean(obj[ABD_ELIGIBILITY]);
      });

      return products?.some(Boolean) || false;
    },

    healthInsuranceHistoryInitialData() {
      return { lhc: null };
    },
    doNotDisplayLHC() {
      let doNotDisplayLhc = true;

      if (this.showNewPartnerSection && this.containsHospital && this.age31OrMore(this.partnerData.newPartner.DOB)) {
        doNotDisplayLhc = false;
      }

      return doNotDisplayLhc;
    },

    formattedProductPolicies() {
      const productObj = this.membershipPolicies?.map((policy) => {
        return {
          eligibleForABD: Boolean(policy.isEligibleForAbd),
          productCode: policy.productNumber,
          productType: convertCodeToValue(
            Object.values(ProductType).map((v, idx) => {
              return { code: idx + 1, value: v };
            }),
            policy.productTypeCode,
          ), // Extras=2, Hospital=1, Package=3
          excesses: policy.productExcess,
          productExcessType: policy.productExcessType,
        };
      });

      return productObj;
    },
    showVeteranCardSection() {
      this.updateCurrentEligibility(true);
      return this.currentEligibility;
    },
    showNewPartnerSection() {
      return !this.oldCompositionHasPartner || !this.partnerData?.existing?.keepAdult;
    },
    containsHospital() {
      const productTypes = this.formattedProductPolicies.map((product) => {
        return product.type ? product.type.toLowerCase() : product.productType.toLowerCase();
      });
      return productTypes.some((type) => {
        return type === ProductType.HOSPITAL_ONLY || type === ProductType.PACKAGE;
      });
    },
    coverExtrasOnly() {
      return this.formattedProductPolicies.every((product) => {
        return product?.productType.toLowerCase() === ProductType.EXTRAS_ONLY.toLowerCase();
      });
    },
    HospitalOrExtrasOnly() {
      if (this.formattedProductPolicies && this.formattedProductPolicies.length > 0 ){
        return this.formattedProductPolicies.every((product) => {
          return product?.productType.toLowerCase() === ProductType.EXTRAS_ONLY.toLowerCase();
        }) || this.formattedProductPolicies.every((product) => {
          return product?.productType.toLowerCase() === ProductType.HOSPITAL_ONLY.toLowerCase();
        });
      }
      return false;
    },
    isPartnerRebateApplicant() {
      const rebateApplicant = this.membershipDetails.rebateApplicant;
      const partner = getPartnerDetailsMP(this.allMembersData, true);
      return partner?.personCode === rebateApplicant;
    },
  },

  methods: {
    ...mapMutations('AddRemovePerson', {
      updateAllMembersDetails: VUEX_CHANGE_COVER.MUTATIONS.updateAllMembersDetails,
      updatePartnerData: VUEX_CHANGE_COVER.MUTATIONS.updatePartnerData,
      updateCurrentComposition: VUEX_CHANGE_COVER.MUTATIONS.updateCurrentComposition,
      updateChildrenData: VUEX_CHANGE_COVER.MUTATIONS.updateChildrenData,
      updateMembershipPolicies: VUEX_CHANGE_COVER.MUTATIONS.updateMembershipPolicies,
      updateMembershipDetails: VUEX_CHANGE_COVER.MUTATIONS.updateMembershipDetails,
      updateCurrentEligibility: VUEX_CHANGE_COVER.MUTATIONS.updateCurrentEligibility,
      updateEligibilityDetails: VUEX_CHANGE_COVER.MUTATIONS.updateEligibilityDetails,
      updateReferenceData: VUEX_CHANGE_COVER.MUTATIONS.updateReferenceData,
      resetAddRemoveVuexStore: VUEX_CHANGE_COVER.MUTATIONS.resetAddRemoveVuexStore,
      updateSubmitPolicyInProcess: VUEX_CHANGE_COVER.MUTATIONS.updateSubmitPolicyInProcess,
    }),

    calculateAgeForChildren(dob) {
      return calculateAge(reverseFormatFormDate(dob));
    },

    getValidExcessesForComposition(composition) {
      const validExcesses = [];

      // convert composition to code, eg. single -> 1, couple -> 2, family -> 3, etc.
      const compositionCode = CoverCompositionCRMCodeMapping.find((item) => {
        return item.value === composition;
      }).code;

      this.membershipPolicies.forEach((product) => {
        if (product.productExcess && product.productExcess.length > 0) {
          if (isEmptyString(product.validToDate) || !isPastDate(reverseFormatFormDate(product.validToDate))) {
            // if validToDate = null/'' or in future, open product, i.e filter out closed excesses
            product.productExcess.forEach((excess) => {
              if (excess.composition.split(',').includes(compositionCode) && (isEmptyString(excess.endDate) || !isPastDate(reverseFormatFormDate(excess.endDate)))) {
                // excess is open (valid) if it has no end date or end date is in the future
                validExcesses.push(excess.excess);
              }
            });
          } else {
            // closed product
            product.productExcess.forEach((excess) => {
              if (excess.composition.split(',').includes(compositionCode)) {
                validExcesses.push(excess.excess); // include all excesses for closed products
              }
            });
          }
        }
      });

      return validExcesses.sort((a, b) => {
        return b - a;
      }); // descending sort based on excess amount value
    },

    doesCurrentExcessAllowComposition(composition) {
      if (this.coverExtrasOnly) {
        return true; // extras only covers don't have any composition/excess restrictions
      }
      const validExcesses = this.getValidExcessesForComposition(composition);
      return validExcesses.length > 0 && validExcesses.includes(this.membershipDetails.excess);
    },

    age31OrMore(dob) {
      const ageAtCoverStartDate = calculateAge(dob);
      return ageAtCoverStartDate >= 31;
    },

    // To add another object for selected contact, concession card, or insurance fund
    addAnotherSelectField(selectedOptions, isConcession) {
      const firstSelection = JSON.parse(JSON.stringify(selectedOptions[0]));
      Object.keys(firstSelection).forEach((key) => {
        firstSelection[key] = '';
      });

      if (isConcession) {
        firstSelection.op = OperationType.ADD;
        firstSelection.startDate = getCurrentDate();
        // firstSelection.id = null;
      }
      selectedOptions.push(firstSelection);
    },

    // To remove an object from selected contact, or insurance fund
    removeSelectField(selectedOptions, index) {
      let selectedData = selectedOptions;
      if (selectedData.length > 1) {
        selectedData = selectedData.splice(index || selectedOptions.length - 1, 1);
      }
    },

    // get the actual display value from the relative api data list
    getDisplayValue(modelValue, listName) {
      let displayValue = '';
      listName.forEach((el) => {
        if (el.returnValue === modelValue) {
          displayValue = el.display;
        }
      });

      return displayValue;
    },

    getDOBErrorMessage(dobField) {
      const errors = dobField.$errors || [];
      if (errors.some(e => e.$validator === 'required')) {
        return `${DOBErrorMessage.IS_EMPTY} Date of birth.`;
      }
      if (errors.some(e => e.$validator === 'isPastDate') && !errors.some(e => e.$validator === 'DATE_VALIDATOR')) {
        return DOBErrorMessage.IS_FUTURE_DATE;
      }
      if (errors.some(e => e.$validator === 'overAge') && !errors.some(e => e.$validator === 'DATE_VALIDATOR')) {
        return DOBErrorMessage.IS_OVERAGE;
      }
      if (errors.some(e => e.$validator === 'underAge') && !errors.some(e => e.$validator === 'DATE_VALIDATOR')) {
        return DOBErrorMessage.IS_UNDERAGE;
      }
      return DOBErrorMessage.IS_INVALID;
    },

    getConcessionNumberErrorMessage(errors) {
      if (errors.some(e => e.$validator === 'required')) return 'Please provide a response for Card number.';
      if (errors.some(e => e.$validator === 'invalidConcessionCardNumber')) return 'Please enter 9 numbers and a letter.';
      return '';
    },

    getConcessionExpiryErrorMessage(errors) {
      if (errors.some(e => e.$validator === 'IS_INVALID')) return 'Please enter the expiry date using the format DD/MM/YYYY.';
      if (errors.some(e => e.$validator === 'isPastDate')) return 'Please try again, expiry dates must be a future date.';
      return '';
    },

    updateCoverComposition() {
      if (this.membershipDetails) {
        this.updateCurrentComposition(this.membershipDetails.composition);
      }
    },

    getDropdownOption(fullOptionList, currentValue, defaultValue) {
      const dropdownOption = fullOptionList.find((option) => {
        return option.value === currentValue;
      });

      if (dropdownOption && dropdownOption.name) {
        return dropdownOption.name;
      }
      return defaultValue;
    },

    getAddress(memberObj, isPostal, isPOBox) {
      if (memberObj) {
        // residential address
        if (!isPostal && !isPOBox) {
          return {
            addressLine1: memberObj.residentialAddress.addressLine1,
            addressLine2: memberObj.residentialAddress.addressLine2 ?? '',
            addressLine3: memberObj.residentialAddress.addressLine3 ?? '',
            suburb: memberObj.residentialAddress.suburb,
            postcode: memberObj.residentialAddress.postcode,
            state: memberObj.residentialAddress.state,
            dpId: memberObj.residentialAddress.dpId ?? '',
          };
        }
        if (isPostal) {
          // addressObj is actually the member object for postal address, hence the need to `addressObj.address`
          const postalAddress = {
            suburb: memberObj.address.suburb,
            postcode: memberObj.address.postcode,
            state: memberObj.address.state,
            dpId: memberObj.address.dpId ?? '',
            addressLine1: memberObj.address.addressLine1,
            addressLine2: memberObj.address.addressLine2 ?? '',
            addressLine3: memberObj.address.addressLine3 ?? '',
          };

          return postalAddress;
        }
      }
      return {};
    },

    isFundAutomaticTransfer(code) {
      const fund = this.fundNamesNewPerson.find((f) => {
        return f.value === code;
      });
      if (fund && fund.isAutomaticTransfer) {
        return fund.isAutomaticTransfer;
      }
      return false;
    },

    async duplicatePersonSearch(firstName, lastName, gender, DOB) {
      this.apiStatus.duplicatePersonCheck.loading = true;
      this.apiStatus.duplicatePersonCheck.fail = false;

      try {
        return await Services.getDuplicatePerson(firstName, lastName, gender, DOB);
      } catch (error) {
        logger.error('Error getting duplicate person: ', JSON.stringify(error.stack));
        this.apiStatus.duplicatePersonCheck.fail = true;
        // default failsafe
        throw new DHLError(error);
      } finally {
        this.apiStatus.duplicatePersonCheck.loading = false;
      }
    },

    async raiseWorkItem(payload) {
      await Services.raiseDuplicatePersonWorkItem(this.memberNumber, payload);
    },

    getDependantList() {
      const childrenArray = [
        RelationshipValue.DAUGHTER.toLowerCase(),
        RelationshipValue.SON.toLowerCase(),
        RelationshipValue.STEP_DAUGHTER.toLowerCase(),
        RelationshipValue.STEP_SON.toLowerCase(),
        RelationshipValue.GRAND_CHILD.toLowerCase(),
        RelationshipValue.BABY.toLowerCase(),
        RelationshipValue.FOSTER_CHILD.toLowerCase(),
        RelationshipValue.OTHER.toLowerCase(),
      ];
      return childrenArray;
    },

    getSelectOptionsLeft,
  },
};
